/**
 * Capacitor Initialization
 * 
 * This file initializes Capacitor plugins and handles native app functionality.
 * Import this file at the top of your main App.js or index.js
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Initialize Capacitor plugins
 */
export const initializeCapacitor = async () => {
  console.log('[Capacitor] Initializing...');
  console.log('[Capacitor] Platform:', Capacitor.getPlatform());
  console.log('[Capacitor] Is Native:', Capacitor.isNativePlatform());
  
  if (!Capacitor.isNativePlatform()) {
    console.log('[Capacitor] Running on web, skipping native initialization');
    return;
  }
  
  try {
    // Initialize Status Bar
    await initializeStatusBar();
    
    // Initialize Keyboard handling
    await initializeKeyboard();
    
    // Initialize App listeners
    await initializeAppListeners();
    
    // Hide splash screen after a short delay
    setTimeout(async () => {
      try {
        await SplashScreen.hide();
        console.log('[Capacitor] Splash screen hidden');
      } catch (e) {
        console.warn('[Capacitor] Error hiding splash screen:', e);
      }
    }, 500);
    
    console.log('[Capacitor] Initialization complete');
  } catch (error) {
    console.error('[Capacitor] Initialization error:', error);
  }
};

/**
 * Initialize Status Bar styling
 */
const initializeStatusBar = async () => {
  try {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'ios') {
      await StatusBar.setStyle({ style: Style.Dark });
    } else if (platform === 'android') {
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
    
    console.log('[Capacitor] Status bar configured');
  } catch (error) {
    console.warn('[Capacitor] Status bar error:', error);
  }
};

/**
 * Initialize Keyboard handling
 */
const initializeKeyboard = async () => {
  try {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'ios') {
      // iOS keyboard settings
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
      await Keyboard.setScroll({ isDisabled: false });
    }
    
    // Listen for keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('[Capacitor] Keyboard will show, height:', info.keyboardHeight);
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
      document.body.classList.add('keyboard-visible');
    });
    
    Keyboard.addListener('keyboardWillHide', () => {
      console.log('[Capacitor] Keyboard will hide');
      document.body.style.setProperty('--keyboard-height', '0px');
      document.body.classList.remove('keyboard-visible');
    });
    
    console.log('[Capacitor] Keyboard configured');
  } catch (error) {
    console.warn('[Capacitor] Keyboard error:', error);
  }
};

/**
 * Initialize App lifecycle listeners
 */
const initializeAppListeners = async () => {
  try {
    // Handle app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('[Capacitor] App state changed, active:', isActive);
      
      // Dispatch custom event for React components to listen to
      window.dispatchEvent(new CustomEvent('appStateChange', { 
        detail: { isActive } 
      }));
    });
    
    // Handle back button (Android)
    App.addListener('backButton', ({ canGoBack }) => {
      console.log('[Capacitor] Back button pressed, canGoBack:', canGoBack);
      
      if (canGoBack) {
        window.history.back();
      } else {
        // Confirm exit or minimize app
        App.minimizeApp();
      }
    });
    
    // Handle deep links
    App.addListener('appUrlOpen', (event) => {
      console.log('[Capacitor] App URL opened:', event.url);
      
      // Handle deep links here
      // Example: passporttalk://translate?lang=es
      const url = new URL(event.url);
      if (url.protocol === 'passporttalk:') {
        // Handle custom URL scheme
        window.dispatchEvent(new CustomEvent('deepLink', { 
          detail: { url: event.url, pathname: url.pathname, params: url.searchParams } 
        }));
      }
    });
    
    console.log('[Capacitor] App listeners configured');
  } catch (error) {
    console.warn('[Capacitor] App listeners error:', error);
  }
};

/**
 * Check if running on native platform
 */
export const isNative = () => Capacitor.isNativePlatform();

/**
 * Get current platform
 */
export const getPlatform = () => Capacitor.getPlatform();

/**
 * Check if running on iOS
 */
export const isIOS = () => Capacitor.getPlatform() === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = () => Capacitor.getPlatform() === 'android';

export default {
  initializeCapacitor,
  isNative,
  getPlatform,
  isIOS,
  isAndroid,
};
